import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { Skeleton } from 'primereact/skeleton';
import PoMisTableInitial from './PoMisTableInitial';
import MonthlyPoMisToolbarHeading from './MonthlyPoMisToolbarHeading';
import './PoMis.scss';
import loadingPuzzleGif from '../../../Assets/images/svg/loading puzzle.gif';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MIS_PO, MIS_MONTHLY_PO } from '../../../Api/resources/endPoint';
import { useAuth } from '../../../Contexts/AuthContext';

/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */

dayjs.extend(customParseFormat);

const getPoMisApiUrl = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MIS_PO}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MIS_PO}`;
};

const getMonthlyPoMisApiUrl = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MIS_MONTHLY_PO}`;
  }
  return `${baseUrl}:${servicePort}${serviceURL}/api${MIS_MONTHLY_PO}`;
};

const TABLES_CONFIG = [{
  apiUrl: getMonthlyPoMisApiUrl(),
  toolBarHeading: 'Monthly PO MIS',
  toolBarHeadingNode: <MonthlyPoMisToolbarHeading />,
  useCustomBodyRenderer: true,
}, {
  apiUrl: getPoMisApiUrl(),
  toolBarHeading: 'PO MIS Details',
  useCustomBodyRenderer: false,
}];

const toastErrorStyle = { backgroundColor: '#bc4545', color: '#FFFFFF', borderRadius: '12px', fontFamily: 'Mukta' };

const showPoMisErrorToast = (message) => toast.error(message, { position: 'top-center', style: toastErrorStyle });

const getPoMisExportFileName = (toolBarHeading = 'PO MIS Details') => (
  String(toolBarHeading || 'PO MIS Details').replace(/\s+/g, '_')
);

const normalizeFilterText = (value) => (
  String(value ?? '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
);

const getFilterSearchText = (filter = {}) => (
  [
    filter.field,
    filter.header,
    filter.label,
    filter.name,
    filter.filterName,
    filter.columnName,
  ]
    .map(normalizeFilterText)
    .filter(Boolean)
    .join(' ')
);

const hasAppliedFilterValue = (filter = {}) => (
  (Array.isArray(filter.selectedValues) && filter.selectedValues.length > 0)
  || (Array.isArray(filter.range) && filter.range.some(Boolean))
  || Boolean(filter.selectedValue)
  || Boolean(filter.value)
  || Boolean(filter.inputValue)
);

const isActiveDateFilter = (filter) => getFilterSearchText(filter).includes('active date');

const isDateFilter = (filter) => {
  const text = getFilterSearchText(filter);
  return text.includes('date') || text.includes('timeline');
};

const isConflictingDateFilter = (filter) => {
  const filterText = getFilterSearchText(filter);
  return !filterText.includes('active date') && (filterText.includes('date') || filterText.includes('timeline'));
};

const hasActiveDateConflict = (appliedFilters = []) => {
  const activeDateSelected = appliedFilters.some(
    (filter) => isActiveDateFilter(filter) && hasAppliedFilterValue(filter),
  );
  const otherDateSelected = appliedFilters.some(
    (filter) => isConflictingDateFilter(filter) && hasAppliedFilterValue(filter),
  );
  return activeDateSelected && otherDateSelected;
};

const hasActiveDateApplied = (filters = []) => (
  filters.some((filter) => isActiveDateFilter(filter) && hasAppliedFilterValue(filter))
);

const cloneFilterConfig = (columns = []) => (
  columns.map((column = {}) => ({
    ...column,
    menuItems: Array.isArray(column.menuItems)
      ? column.menuItems.map((menuItem = {}) => ({
        ...menuItem,
        items: Array.isArray(menuItem.items)
          ? menuItem.items.map((item = {}) => ({
            ...item,
            selectedValues: Array.isArray(item.selectedValues)
              ? [...item.selectedValues]
              : item.selectedValues,
            range: Array.isArray(item.range) ? [...item.range] : item.range,
          }))
          : menuItem.items,
      }))
      : column.menuItems,
  }))
);

const hasFilterMenuItems = (column = {}) => (
  Array.isArray(column?.menuItems)
  && column.menuItems.some((menuItem = {}) => Array.isArray(menuItem?.items) && menuItem.items.length > 0)
);

// Overlay applied filter values onto the column structure so the panel reopens with selections intact.
const stampAppliedValues = (columns = [], appliedFilters = []) => {
  if (!appliedFilters.length) return cloneFilterConfig(columns);
  return columns.map((column = {}) => ({
    ...column,
    menuItems: Array.isArray(column.menuItems)
      ? column.menuItems.map((menuItem = {}) => ({
        ...menuItem,
        items: Array.isArray(menuItem.items)
          ? menuItem.items.map((item = {}) => {
            const match = appliedFilters.find((af) => af.field === item.field);
            if (!match) return { ...item };
            return {
              ...item,
              range: Array.isArray(match.range) ? [...match.range] : item.range,
              selectedValues: Array.isArray(match.selectedValues) ? [...match.selectedValues] : item.selectedValues,
              selectedValue: match.selectedValue !== undefined ? match.selectedValue : item.selectedValue,
              value: match.value !== undefined ? match.value : item.value,
              inputValue: match.inputValue !== undefined ? match.inputValue : item.inputValue,
            };
          })
          : menuItem.items,
      }))
      : column.menuItems,
  }));
};

const getGlobalFilterColumns = (tableList = []) => {
  const globalFilterColumns = tableList.flatMap((tbl) => (
    tbl?.configuration?.tableSettings?.tableConfig || []
  )).filter((col) => col.isGlobalFilter);
  // as per the requirement of production
  const dedupedColumns = new Map();

  globalFilterColumns.forEach((column) => {
    const key = getFilterSearchText(column);
    const existingColumn = dedupedColumns.get(key);

    if (!existingColumn) {
      dedupedColumns.set(key, column);
      return;
    }

    if (!hasFilterMenuItems(existingColumn) && hasFilterMenuItems(column)) {
      dedupedColumns.set(key, column);
    }
  });

  return Array.from(dedupedColumns.values());
};

const poMisDetailsRightAlignedHeaders = new Set([
  'Project Id',
  'Zoho Client Id',
  '% (Of Contract Value)',
  'Amount',
  'Invoice Amount',
  'Total PO Amount',
  'Balance Amount',
  'Remaining Days',
].map(normalizeFilterText));

const resetPoMisDetailsCellStyles = (cell) => {
  if (!cell) return;
  const cellContent = cell.querySelector(':scope > div');
  cell.style.removeProperty('text-align');
  if (!cellContent) return;
  cellContent.style.removeProperty('width');
  cellContent.style.removeProperty('min-width');
  cellContent.style.removeProperty('padding');
  cellContent.style.removeProperty('display');
  cellContent.style.removeProperty('justify-content');
  cellContent.style.removeProperty('align-items');
  cellContent.style.removeProperty('text-align');
  cellContent.style.removeProperty('white-space');
  cellContent.querySelectorAll('*').forEach((childElement) => {
    childElement.style.removeProperty('margin');
    childElement.style.removeProperty('padding');
    childElement.style.removeProperty('text-align');
  });
};

const syncPoMisDetailsAlignment = () => {
  const tableRoot = document.querySelector('.custom-table-po-mis-details .p-datatable');
  if (!tableRoot) return;

  const headerCells = tableRoot.querySelectorAll('.p-datatable-thead > tr > th');
  const bodyRows = tableRoot.querySelectorAll('.p-datatable-tbody > tr');

  headerCells.forEach((headerCell, index) => {
    const headerText = normalizeFilterText(
      headerCell.querySelector('.tu-header-line')?.textContent || headerCell.textContent || '',
    );
    const isRightAligned = poMisDetailsRightAlignedHeaders.has(headerText);
    const isCentered = headerText === normalizeFilterText('PO Link');
    const isStatus = headerText === normalizeFilterText('Project Status');
    const isEllipsis = [
      normalizeFilterText('Delivery Manager/Sr. Manager/Project Manager'),
      normalizeFilterText('PO Timeline'),
    ].includes(headerText);

    headerCell.classList.toggle('po-mis-col-right', isRightAligned);
    headerCell.classList.toggle('po-mis-col-center', isCentered);
    headerCell.classList.toggle('po-mis-col-status', isStatus);
    headerCell.classList.toggle('po-mis-col-ellipsis', isEllipsis);

    bodyRows.forEach((row) => {
      const bodyCell = row.children[index];
      if (!bodyCell) return;
      resetPoMisDetailsCellStyles(bodyCell);
      bodyCell.classList.toggle('po-mis-col-right', isRightAligned);
      bodyCell.classList.toggle('po-mis-col-center', isCentered);
      bodyCell.classList.toggle('po-mis-col-status', isStatus);
      bodyCell.classList.toggle('po-mis-col-ellipsis', isEllipsis);
    });
  });
};

const LocalSkeleton = () => (
  <div className="skeleton-loader">
    {[1, 2].map((key) => (
      <div key={key} className='skeleton-card'>
        <div className='skeleton-header'>
          <Skeleton width="180px" height="1.5rem" />
          <div className="skeleton-header-actions">
            <Skeleton width="80px" height="1.5rem" />
            <Skeleton width="80px" height="1.5rem" />
          </div>
        </div>
        <div className="skeleton-body-content">
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
          <Skeleton width="100%" height="2rem" />
        </div>
      </div>
    ))}
  </div>
);

const PomisIndex = () => {
  const { userProfile } = useAuth();
  const userProfileRef = useRef(userProfile);
  const validGlobalFilterColumnsRef = useRef([]);
  const initialGlobalFilterColumnsRef = useRef([]);
  const activeDateAppliedRef = useRef(false);
  userProfileRef.current = userProfile;

  const [tables, setTables] = useState([
    { configuration: null, data: [], toolBarHeading: 'Monthly PO MIS' },
    { configuration: null, data: [], toolBarHeading: 'PO MIS Details' },
  ]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasAppliedFiltersOnce, setHasAppliedFiltersOnce] = useState(false);
  const [globalFilterColumns, setGlobalFilterColumns] = useState([]);

  // ==========================================
  // STATE MANAGEMENT TO FIX EXPORT FILTER PAYLOAD
  // ==========================================
  const [globalFilters, setGlobalFilters] = useState([]);
  const [globalSort, setGlobalSort] = useState(null);
  const [filterPanelResetKey, setFilterPanelResetKey] = useState(0);
  const lastAppliedFiltersRef = useRef([]);
  const globalSortRef = useRef(globalSort);
  globalSortRef.current = globalSort;

  const handleFilterUpdate = ({ action, filters, tableConfiguration }) => {
    if (action === 'reset') {
      validGlobalFilterColumnsRef.current = [];
      activeDateAppliedRef.current = false;
      lastAppliedFiltersRef.current = [];
      setFilterPanelResetKey((prev) => prev + 1);
      setGlobalFilterColumns(cloneFilterConfig(initialGlobalFilterColumnsRef.current));
      setGlobalFilters([]);
      setHasAppliedFiltersOnce(false);
      // Explicitly clear the tables to force the PoMisTableInitial state to appear immediately
      setTables([
        { configuration: null, data: [], toolBarHeading: 'Monthly PO MIS' },
        { configuration: null, data: [], toolBarHeading: 'PO MIS Details' },
      ]);
      return;
    }

    if (action !== 'apply') {
      return;
    }

    if (hasActiveDateConflict(filters || [])) {
      showPoMisErrorToast(
        'PO Active Date is already selected. Please remove it before selecting another date filter.',
      );
      setGlobalFilterColumns(stampAppliedValues(initialGlobalFilterColumnsRef.current, lastAppliedFiltersRef.current));
      return;
    }

    if (Array.isArray(tableConfiguration) && tableConfiguration.length > 0) {
      activeDateAppliedRef.current = hasActiveDateApplied(filters || []);
      // No globalFilterColumns update here the global panel manages its own state.
    }
  };

  useEffect(() => {
    const handleDateFilterClick = (event) => {
      const filterItem = event.target.closest('.tu-filter-column-item, .tu-filter-subitem');
      if (!filterItem || !activeDateAppliedRef.current) return;
      const filterLabel = normalizeFilterText(filterItem.textContent);
      const isOtherDateFilter
        = (filterLabel.includes('date') || filterLabel.includes('timeline')) && !filterLabel.includes('active date');

      if (!isOtherDateFilter) return;

      event.preventDefault();
      event.stopPropagation();
      showPoMisErrorToast(
        'PO Active Date is already selected. Please remove it before selecting another date filter.',
      );
    };

    document.addEventListener('click', handleDateFilterClick, true);
    return () => document.removeEventListener('click', handleDateFilterClick, true);
  }, []);

  useEffect(() => {
    if (userProfile?.email) {
      handleGlobalFilterApply([], { skipDateValidation: true });
    }
  }, [Boolean(userProfile?.email)]);

  useEffect(() => {
    const alignmentFrame = window.requestAnimationFrame(syncPoMisDetailsAlignment);
    const alignmentTimeout = window.setTimeout(syncPoMisDetailsAlignment, 150);
    return () => {
      window.cancelAnimationFrame(alignmentFrame);
      window.clearTimeout(alignmentTimeout);
    };
  }, [tables, loading]);

  const handleGlobalSortApply = async (field, order, direction) => {
    const newSort = { field, order, direction };
    await handleGlobalFilterApply(lastAppliedFiltersRef.current, { currentSort: newSort });
    setGlobalSort(newSort);
  };

  const handleGlobalFilterApply = async (appliedFilters, options = {}) => {
    const currentUserProfile = userProfileRef.current;
    const { skipDateValidation = false, currentSort = globalSortRef.current } = options;

    lastAppliedFiltersRef.current = appliedFilters;
    setGlobalFilters(appliedFilters || []); // Setting state to pass down to TableUtility for export

    if (hasActiveDateConflict(appliedFilters || [])) {
      showPoMisErrorToast(
        'PO Active Date is already selected. Please remove it before selecting another date filter.',
      );
      setGlobalFilterColumns(stampAppliedValues(initialGlobalFilterColumnsRef.current, lastAppliedFiltersRef.current));
      return;
    }

    const filterPayloadArray = [...(appliedFilters || [])];

    if (currentSort) {
      const sortIndex = filterPayloadArray.findIndex((value) => value.type === 'sort');
      if (sortIndex > -1) filterPayloadArray.splice(sortIndex, 1);
      filterPayloadArray.push({
        type: 'sort',
        field: currentSort.field,
        sortDirection: currentSort.direction,
        sortOrder: currentSort.order,
      });
    }

    const hasDateRange = filterPayloadArray.some(
      (value) => Array.isArray(value.range) && value.range.length === 2 && value.range[0] && value.range[1],
    );

    const hasNonDateFilters = filterPayloadArray.some(
      (value) => !isDateFilter(value) && hasAppliedFilterValue(value),
    );

    // ==========================================
    // VALIDATION: Non-date filters require a date range
    // ==========================================
    if (!skipDateValidation) {
      if (hasNonDateFilters && !hasDateRange) {
        showPoMisErrorToast('Please select any one of the date range first.');
        setFilterPanelResetKey((prev) => prev + 1);
        setGlobalFilterColumns(
          stampAppliedValues(initialGlobalFilterColumnsRef.current, lastAppliedFiltersRef.current),
        );
        return;
      }
      if (!hasDateRange && !hasNonDateFilters) {
        showPoMisErrorToast('Please select a date range before applying filters.');
        setFilterPanelResetKey((prev) => prev + 1);
        setGlobalFilterColumns(
          stampAppliedValues(initialGlobalFilterColumnsRef.current, lastAppliedFiltersRef.current),
        );
        return;
      }
      setHasAppliedFiltersOnce(true);
    }

    setLoading(true);
    // Explicitly wipe the tables structure prior to fetching. This guarantees that LocalSkeleton displays.
    setTables([
      { configuration: null, data: [], toolBarHeading: 'Monthly PO MIS' },
      { configuration: null, data: [], toolBarHeading: 'PO MIS Details' },
    ]);

    const filterPayload = {
      filter: filterPayloadArray,
      ...(currentSort ? {
        sortField: currentSort.field,
        sortOrder: currentSort.direction || (currentSort.order === 1 ? 'ASC' : 'DESC'),
      } : {}),
      page: 1,
      pageSize: 100000,
      userInfo: {
        emailId: currentUserProfile.email,
        permissions: currentUserProfile.permissions,
        roleDetails: currentUserProfile.roleDetails,
        employeeId: currentUserProfile.employeeId,
        globalColors: currentUserProfile?.siteConfiguration?.global_colors,
      },
    };

    try {
      const results = await Promise.all(
        TABLES_CONFIG.map(async (tbl) => {
          const res = await fetch(tbl.apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'token': currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
            },
            body: JSON.stringify(filterPayload),
          });
          const json = await res.json();
          if (!res.ok) {
            throw new Error(json?.message || 'Failed to load data. Please try again.');
          }

          const tableData = json?.data?.tableData || [];
          const tableConfiguration = json?.data?.tableConfiguration || [];
          const tableRowCount = Math.max(tableData.length, 1);
          const tableClassName = `custom-table-${tbl.toolBarHeading.replace(/\s+/g, '-').toLowerCase()}`;
          const exportFileName = getPoMisExportFileName(tbl.toolBarHeading);

          return {
            configuration: {
              generalSettings: {
                enableApiFiltering: false,
                baseURL: tbl.apiUrl,
                showToolBar: true,
                showPagination: false,
                handler: {
                  onFilterUpdate: handleFilterUpdate,
                },
                toolBarSettings: {
                  showToolBarHeading: true,
                  toolBarHeading: tbl.toolBarHeadingNode || tbl.toolBarHeading,
                  showDownloadCsv: true,
                  showDownloadExcel: false,
                  downloadCsvFileName: exportFileName,
                  downloadExcelFileName: exportFileName,
                  downloadExcelSheetName: tbl.toolBarHeading,
                },
                userInfo: {
                  emailId: currentUserProfile.email,
                  permissions: currentUserProfile.permissions,
                  roleDetails: currentUserProfile.roleDetails,
                  employeeId: currentUserProfile.employeeId,
                  globalColors: currentUserProfile?.siteConfiguration?.global_colors,
                },
                headers: {
                  token: currentUserProfile?.access_token || localStorage.getItem('authToken') || '',
                },
              },
              apiEndpoints: {
                getAllViews: { endpoint: '', enabled: true },
                getFilteredData: { endpoint: '', enabled: true },
                saveOrUpdateView: { endpoint: 'view', enabled: true },
                deleteView: { endpoint: 'view-delete', enabled: false },
                download: {
                  endpoint: tbl.toolBarHeading === 'Monthly PO MIS' ? 'monthly/export' : 'export',
                  enabled: true,
                  filename: exportFileName,
                  sheetName: tbl.toolBarHeading,
                },
              },
              tableSettings: {
                enableTreeTable: false,
                useVirtualizedTable: true,
                size: 'small',
                loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
                pageSize: tableRowCount,
                showPageSizeSelector: false,
                paginationConfig: {
                  page: 1,
                  limit: tableRowCount,
                  rowsPerPageOptions: [tableRowCount],
                },
                showExpandCollapse: false,
                tableConfig: tableConfiguration,
                maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
                virtualScrollerOptions: { itemSize: 42 },
                tableClassName,
              },
            },
            data: tableData,
            toolBarHeading: tbl.toolBarHeading,
            useCustomBodyRenderer: tbl.useCustomBodyRenderer ?? false,
          };
        }),
      );

      const nextGlobalFilterColumns = cloneFilterConfig(getGlobalFilterColumns(results));

      // Capture the full filter set only on the very first load the API may return
      // a narrower tableConfiguration on subsequent (filtered) calls.
      if (initialGlobalFilterColumnsRef.current.length === 0) {
        initialGlobalFilterColumnsRef.current = cloneFilterConfig(nextGlobalFilterColumns);
      }

      setTables(results);
      setGlobalFilterColumns(stampAppliedValues(initialGlobalFilterColumnsRef.current, lastAppliedFiltersRef.current));
      validGlobalFilterColumnsRef.current = cloneFilterConfig(initialGlobalFilterColumnsRef.current);
      setLoading(false);
      setIsInitialLoad(false);
    } catch (err) {
      showPoMisErrorToast(err.message || 'An error occurred. Please try again.');
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const allTablesEmpty = tables.every(tbl => !tbl.data || tbl.data.length === 0);

  const emptyStateComponent = (() => {
    if (isInitialLoad && loading) {
      return (
        <div className="empty-state-container loading">
          <img src={loadingPuzzleGif} alt="Loading..." className="loading-puzzle-gif" />
          <span className="loading-state-text">
            Loading details...
          </span>
        </div>
      );
    }

    if (loading) {
      return <LocalSkeleton />;
    }

    if (allTablesEmpty) {
      return hasAppliedFiltersOnce
        ? (
          <div className="empty-state-container">
            <i className="pi pi-inbox empty-state-icon"></i>
            <span className="empty-state-text">
              No data found for the selected filter(s).
            </span>
          </div>
        )
        : <PoMisTableInitial />;
    }

    return undefined;
  })();

  return (
    <div className="po-mis-filter-class filter-class">
      <TableUtility
        tables={tables.map((tbl) => ({
          configuration: tbl.configuration,
          data: tbl.data,
          useCustomBodyRenderer:
          typeof tbl.useCustomBodyRenderer === 'boolean'
            ? tbl.useCustomBodyRenderer
            : false,
        }))}
        globalFilters={globalFilters}
        globalFilterConfig={globalFilterColumns}
        showGlobalFilter
        showFilterOption
        showTieredMenu
        showDownloadCSV
        showDownloadExcel={false}
        showMultiTableExport={false}
        onGlobalFilterApply={handleGlobalFilterApply}
        onGlobalSortApply={handleGlobalSortApply}
        emptyStateComponent={emptyStateComponent}
        globalFilterAlignRight
        renderGlobalFilterTrigger={(globalFilterTrigger) => (
          <div className="po-mis-page-header">
            <span>PO MIS</span>
            <div key={filterPanelResetKey} className="po-mis-page-header-actions">
              {globalFilterTrigger}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default PomisIndex;
